import { isTokenExpired } from './tokenUtils';
import { handleApiError } from './errorHandler';
import { getCurrentCorrelationId, extractCorrelationIdFromHeaders, setCorrelationId } from './correlationId';

// Determine the correct API URL based on environment
const getApiUrl = () => {
  const isDevelopment = import.meta.env.DEV;
  const isProduction = import.meta.env.PROD;
  
  if (isProduction) {
    return import.meta.env.VITE_API_URL || 'http://api-omclaims.flockrinc.com/api';
  }
  
  return import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
};

const BASE_URL = getApiUrl();

/**
 * Base API client that handles authorization and requests
 * Task 2.3: Enhanced with automatic token refresh on 401 using JWT refresh tokens
 */
let logoutCallback = null;
let isRefreshing = false;
let failedQueue = [];

export const setLogoutCallback = (callback) => {
  logoutCallback = callback;
};

/**
 * Process queued requests after token refresh
 */
const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

/**
 * Attempts to refresh the access token using the stored refresh token
 * Implements automatic token refresh using JWT refresh token format
 */
const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem('refreshToken');
  
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }
  
  try {
    // Call refresh endpoint directly without using apiClient to avoid recursion
    const response = await fetch(`${BASE_URL}/auth/refresh-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });
    
    if (!response.ok) {
      throw new Error('Failed to refresh token');
    }
    
    const data = await response.json();
    
    // Handle standardized response format
    const responseData = data.data || data;
    
    // Update stored tokens with new JWT tokens
    localStorage.setItem('token', responseData.accessToken);
    if (responseData.refreshToken) {
      localStorage.setItem('refreshToken', responseData.refreshToken);
    }
    
    return responseData.accessToken;
  } catch (error) {
    // Clear all tokens on refresh failure
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    throw error;
  }
};

/**
 * Redirects user to login page and clears invalid token
 */
const redirectToLogin = () => {
  localStorage.removeItem('user');
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  window.location.href = '/login';
};
/**
 * Validates JWT token and handles authentication errors
 * @param {string} token - JWT token to validate
 * @returns {boolean} - true if token is valid, false otherwise
 */
const validateToken = (token) => {
  if (!token) {
    return false;
  }
  
  if (isTokenExpired(token)) {
    return false;
  }
  
  return true;
};

const apiClient = {
  // Handles response processing and error handling
  async handleResponse(response, endpoint = '') {
    // Handle authentication errors for non-public endpoints
    if (response.status === 401) {
      console.error('Authentication failed. Token may be invalid or expired.');
      
      // Clear invalid token and redirect to login
      localStorage.removeItem('token');
      
      // Trigger logout if callback is set for 401 (unauthorized)
      if (logoutCallback) {
        setTimeout(() => logoutCallback(), 100); // Small delay to avoid immediate state conflicts
      } else {
        // Fallback redirect if no callback is set
        redirectToLogin();
      }
      
      // Create error object and use handleApiError for consistent processing
      const error = new Error('Your session has expired. Please log in again.');
      error.status = 401;
      error.response = { status: 401 };
      throw handleApiError(error);
    } else if (response.status === 403) {
      // For 403 (forbidden), only logout if it's an auth-related endpoint
      // Admin users may get 403 on operational endpoints like claims, which is expected
      const isAuthEndpoint = endpoint.includes('/auth') || endpoint.includes('/profile') || endpoint.includes('/me');
      
      if (isAuthEndpoint) {
        console.error('Access forbidden for authentication endpoint. Clearing session.');
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        
        if (logoutCallback) {
          setTimeout(() => logoutCallback(), 100);
        } else {
          redirectToLogin();
        }
        
        // Create error object and use handleApiError for consistent processing
        const error = new Error('Access denied. Please log in with proper credentials.');
        error.status = 403;
        error.response = { status: 403 };
        throw handleApiError(error);
      }
    }
    
    // Try to parse JSON response
    let data;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    // Extract correlation ID from response headers if present
    const responseCorrelationId = extractCorrelationIdFromHeaders(response.headers);
    if (responseCorrelationId) {
      setCorrelationId(responseCorrelationId);
    }

    if (!response.ok) {
      // Create error object with response data for handleApiError
      const error = new Error(`Request failed with status ${response.status}`);
      error.status = response.status;
      error.response = {
        status: response.status,
        data: data
      };
      
      // Add correlation ID to error for tracking
      if (responseCorrelationId) {
        error.correlationId = responseCorrelationId;
      }
      
      // Use handleApiError for standardized error processing
      throw handleApiError(error);
    }

    return { 
      data,
      status: response.status,
      headers: response.headers,
      correlationId: responseCorrelationId
    };
  },

  // Base request method with automatic token refresh on 401
  async request(endpoint, options = {}) {
    const url = `${BASE_URL}/${endpoint.replace(/^\//, '')}`;
    
    // Get token from localStorage
    let token = localStorage.getItem('token');
    
    // Check if this is a public endpoint that doesn't require authentication
    const isPublicEndpoint = endpoint.includes('auth/login') || 
                            endpoint.includes('auth/register') || 
                            endpoint.includes('auth/refresh-token') ||
                            endpoint.includes('health') ||
                            endpoint.includes('public');
    
    // For non-public endpoints, validate token before making request
    if (!isPublicEndpoint && !validateToken(token)) {
      redirectToLogin();
      
      // Cancel the request with appropriate error
      const error = new Error('Authentication required. Please log in to access this resource.');
      error.status = 401;
      error.response = { status: 401 };
      throw handleApiError(error);
    }
    
    // Get or generate correlation ID for request tracking
    const correlationId = getCurrentCorrelationId();
    
    const config = {
      ...options,
      headers: {
        // Add correlation ID for request tracking
        'X-Correlation-ID': correlationId,
        // Only add Authorization header if token is valid and not a public endpoint
        ...((!isPublicEndpoint && validateToken(token)) ? { 'Authorization': `Bearer ${token}` } : {}),
        ...options.headers
      }
    };

    // Don't set Content-Type for FormData
    if (!(options.body instanceof FormData) && !config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }

    // DEBUG: Log request details for status update endpoints
    if (endpoint.includes('status') || endpoint.includes('sla')) {
      console.log('API Client Debug:', {
        endpoint,
        method: config.method || 'GET',
        url,
        correlationId,
        hasToken: !!token,
        tokenStart: token?.substring(0, 20) + '...',
        authHeader: config.headers['Authorization']?.substring(0, 30) + '...',
        payload: config.body
      });
    }
    
    let response;
    try {
      response = await fetch(url, config);
    } catch (fetchError) {
      console.error('❌ [ApiClient] Network error during fetch:', {
        url,
        error: fetchError.message,
        errorName: fetchError.name,
        errorType: typeof fetchError
      });
      throw fetchError;
    }
    
    // Handle 401 Unauthorized with automatic token refresh
    if (response.status === 401 && !isPublicEndpoint) {
      // If we're already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          // Retry original request with new token and correlation ID
          config.headers['Authorization'] = `Bearer ${token}`;
          config.headers['X-Correlation-ID'] = correlationId;
          return fetch(url, config);
        }).then(async (response) => {
          return this.handleResponse(response, endpoint);
        });
      }
      
      console.log('Access token expired, attempting refresh...');
      isRefreshing = true;
      
      try {
        // Attempt to refresh the access token using stored JWT refresh token
        const newAccessToken = await refreshAccessToken();
        
        // Process any queued requests
        processQueue(null, newAccessToken);
        
        // Retry the original request with the new token and correlation ID
        config.headers['Authorization'] = `Bearer ${newAccessToken}`;
        config.headers['X-Correlation-ID'] = correlationId;
        response = await fetch(url, config);
        
        isRefreshing = false;
      } catch (refreshError) {
        // Refresh failed, process queue with error and redirect to login
        processQueue(refreshError, null);
        isRefreshing = false;
        
        console.error('Token refresh failed:', refreshError.message);
        
        // Trigger logout callback or redirect to login
        if (logoutCallback) {
          setTimeout(() => logoutCallback(), 100);
        } else {
          redirectToLogin();
        }
        
        // Throw authentication error
        const authError = new Error('Your session has expired. Please log in again.');
        authError.status = 401;
        authError.response = { status: 401 };
        throw handleApiError(authError);
      }
    }
    
    return this.handleResponse(response, endpoint);
  },

  // GET method with query parameter support
  async get(endpoint, options = {}) {
    let finalEndpoint = endpoint;
    
    // Handle params option for query parameters
    if (options.params) {
      console.log('🔧 apiClient.get: Processing query parameters:', {
        endpoint,
        params: options.params,
        paramsType: typeof options.params,
        paramsKeys: Object.keys(options.params)
      });
      
      // Convert params object to URLSearchParams for proper encoding
      const searchParams = new URLSearchParams();
      
      for (const [key, value] of Object.entries(options.params)) {
        if (value !== null && value !== undefined && value !== '') {
          searchParams.append(key, String(value));
        }
      }
      
      // Append query string to endpoint if we have parameters
      if (searchParams.toString()) {
        const separator = endpoint.includes('?') ? '&' : '?';
        finalEndpoint = `${endpoint}${separator}${searchParams.toString()}`;
        
        console.log('✅ apiClient.get: Query parameters added to URL:', {
          originalEndpoint: endpoint,
          finalEndpoint: finalEndpoint,
          queryString: searchParams.toString()
        });
      }
      
      // Remove params from options to avoid passing to request()
      const { params, ...requestOptions } = options;
      options = requestOptions;
    }
    
    console.log('🌐 apiClient.get: Making GET request:', {
      finalEndpoint,
      hasQueryParams: finalEndpoint.includes('?'),
      method: 'GET'
    });
    
    return this.request(finalEndpoint, {
      method: 'GET',
      ...options
    });
  },

  // POST method
  post(endpoint, data) {
    console.log('🌐 apiClient.post called', {
      endpoint,
      dataType: data?.constructor?.name,
      isFormData: data instanceof FormData,
      formDataKeys: data instanceof FormData ? Array.from(data.keys()) : null,
      jsonData: !(data instanceof FormData) ? data : '[FormData]'
    });

    const options = {
      method: 'POST',
    };

    // Handle FormData (for file uploads)
    if (data instanceof FormData) {
      console.log('📁 apiClient: Using FormData for file upload', {
        formDataEntries: Array.from(data.entries()).map(([key, value]) => [
          key, 
          value instanceof File ? `File: ${value.name} (${value.size} bytes)` : value
        ])
      });
      options.body = data;
      // Don't set Content-Type header for FormData - let the browser set it with boundary
    } else {
      console.log('📝 apiClient: Using JSON data', { data });
      options.body = JSON.stringify(data);
      options.headers = {
        ...(options.headers || {}),
        'Content-Type': 'application/json',
      };
    }

    console.log('🚀 apiClient: Making POST request', {
      endpoint,
      url: `${getApiUrl()}/${endpoint.replace(/^\//, '')}`,
      method: options.method,
      hasBody: !!options.body,
      headers: options.headers
    });

    return this.request(endpoint, options);
  },
  
  // PUT method
  put(endpoint, data) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
      }
    });
  },
  
  // PATCH method
  patch(endpoint, data) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
      }
    });
  },
  
  // DELETE method
  delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  },

  // Token management utilities for unified authentication
  setToken(accessToken, refreshToken = null) {
    localStorage.setItem('token', accessToken);
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
  },

  getToken() {
    return localStorage.getItem('token');
  },

  getRefreshToken() {
    return localStorage.getItem('refreshToken');
  },

  clearTokens() {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
  }
};

// Export as both named and default for backward compatibility
export { apiClient };
export default apiClient;
